export interface AppConfig {
  clientId: string;
  /** Full OIDC authorization endpoint URL */
  authorizeUrl: string;
  /** Full OIDC token endpoint URL */
  tokenUrl: string;
  /** Space-separated scopes to request at login */
  scopes: string;
  /** SkyMap Id's profile page; absent when the IdP has none (Cognito) */
  profileUrl?: string;
  /** Import channels available per API, e.g. { skymap: ["production", "staging"] } */
  specSources?: Record<string, string[]>;
}

let cached: AppConfig | null = null;

/**
 * Fetches the provider-neutral auth config from the backend on first call,
 * then caches it. Local dev can override via .env.local (VITE_AUTH_* — e.g. to
 * point at a deployed IdP) to avoid needing the deployed API running locally.
 */
export async function getConfig(): Promise<AppConfig> {
  if (cached) {
    return cached;
  }

  if (import.meta.env.DEV && import.meta.env.VITE_AUTH_CLIENT_ID) {
    cached = {
      clientId: import.meta.env.VITE_AUTH_CLIENT_ID as string,
      authorizeUrl: import.meta.env.VITE_AUTH_AUTHORIZE_URL as string,
      tokenUrl: import.meta.env.VITE_AUTH_TOKEN_URL as string,
      scopes: (import.meta.env.VITE_AUTH_SCOPES as string | undefined) ?? 'email openid profile',
      profileUrl: import.meta.env.VITE_AUTH_PROFILE_URL as string | undefined,
    };
    return cached;
  }

  const res = await fetch('/api/v1/config');
  if (!res.ok) {
    throw new Error('Failed to load app config');
  }
  cached = (await res.json()) as AppConfig;
  return cached;
}
